import { apiClient } from "@/services/apiClient";
import { Client, SocialProfiles } from "@/types";

interface RawClientData {
  uuid: string;
  clientName: string;
  tag: string;
  notionPageId: string;
  notionUrl: string;
  createdAt: string | null;
  lastUpdated: string | null;
  status?: string;
  slug?: string;
  proposalToken?: string;
  stats: {
    lastActivity: string | null;
    totalImages: number;
  };
  instagramStartFollowers?: number;
  facebookStartFollowers?: number;
  tiktokStartFollowers?: number;
  relationshipStartedAt?: string;
  websiteUrl?: string | null;
  socialProfiles?: SocialProfiles;
  locale?: string;
  sector?: string;
  postsPerWeek?: number;
  latitude?: number | null;
  longitude?: number | null;
  location?: string | null;
  businessType?: string | null;
  serviceArea?: string | null;
  monthlyAdBudgetDefault?: number | null;
  autoBoostEnabled?: boolean;
  boostStrategy?: string | null;
  autoFetchIdeasEnabled?: boolean;
  isPortfolioVisible?: boolean;
  googleAdsCustomerId?: string | null;
  googleAdsClientPays?: boolean;
  googleAdsBilledFrom?: string | null;
  accessRole?: "owner" | "member";
}

export const clientService = {
  async createClient(
    clientName: string,
    expectedFacebookPageName?: string,
    expectedInstagramUsername?: string
  ): Promise<{
    success: boolean;
    client?: Client;
    error?: string;
  }> {
    try {
      const response = await apiClient.post(
        `/client/create-full/${encodeURIComponent(clientName)}`,
        {
          expected_facebook_page_name: expectedFacebookPageName,
          expected_instagram_username: expectedInstagramUsername,
        }
      );

      if (response.success) {
        const rawClient = response.client;

        const client: Client = {
          id: rawClient.uuid,
          clientName: rawClient.clientName,
          notionUrl: rawClient.notionUrl || "",
          createdAt: rawClient.createdAt || new Date().toISOString(),
          lastUpdated: rawClient.lastUpdated || new Date().toISOString(),
          stats: rawClient.stats || {
            lastActivity: null,
            totalImages: 0,
          },
        };

        return { success: true, client };
      } else {
        return { success: false, error: response.message };
      }
    } catch (error: any) {
      console.error("❌ Error creating client:", error);

      if (error.response?.status === 409) {
        return { success: false, error: "Client already exists" };
      }
      if (error.response?.status === 500) {
        return { success: false, error: "Server error while creating client" };
      }
      if (error.response?.status === 401) {
        return { success: false, error: "Authentication required" };
      }
      if (error.response?.status === 403) {
        return {
          success: false,
          error:
            "Access denied. You are not authorized to use this application.",
        };
      }

      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to create client",
      };
    }
  },

  async deleteClient(clientUuid: string): Promise<{
    success: boolean;
    clientName: string;
    postsDeleted: number;
    gridsDeleted: number;
    mediaItemsDeleted: number;
    errors: string[];
    error?: string;
  }> {
    try {
      const response = await apiClient.delete(`/client/${clientUuid}`);

      return response;
    } catch (error: any) {
      console.error("❌ Error deleting client:", error);
      if (error.response?.status === 404) {
        throw new Error("Client not found");
      }
      if (error.response?.status === 500) {
        throw new Error("Server error while deleting client");
      }
      if (error.response?.status === 401) {
        throw new Error("Authentication required");
      }
      if (error.response?.status === 403) {
        throw new Error(
          "Access denied. Only octavianrotari@beenerdy.co can access this application.",
        );
      }
      throw new Error(
        error.response?.data?.error ||
          `HTTP ${error.response?.status}: Failed to delete client`,
      );
    }
  },

  async getClients(includeArchived: boolean = false): Promise<Client[]> {
    try {
      const url = includeArchived ? "/clients?include_archived=true" : "/clients";
      const clientsData = await apiClient.get(url);

      let rawClients: RawClientData[];

      if (Array.isArray(clientsData)) {
        rawClients = clientsData;
      } else if (clientsData.clients && Array.isArray(clientsData.clients)) {
        rawClients = clientsData.clients;
      } else if (clientsData.uuid) {
        rawClients = [clientsData];
      } else {
        console.error("❌ Unexpected response format:", clientsData);
        throw new Error("Invalid response format: expected client data");
      }

      return rawClients.map(this._transformClient);
    } catch (error: any) {
      console.error("❌ Error fetching clients:", error);

      if (error.response?.status === 404) {
        throw new Error("Clients endpoint not found - check API server");
      }
      if (error.response?.status === 500) {
        throw new Error("Server error while fetching clients");
      }
      if (error.response?.status === 401) {
        throw new Error("Authentication required");
      }
      if (error.response?.status === 403) {
        throw new Error(
          "Access denied. Only octavianrotari@beenerdy.co can access this application.",
        );
      }
      throw new Error(
        error.response?.data?.error ||
          `HTTP ${error.response?.status}: Failed to fetch clients`,
      );
    }
  },

  async getClientById(id: string): Promise<Client> {
    try {
      const clients = await this.getClients(true);
      const client = clients.find((c) => c.id === id);

      if (!client) {
        throw new Error(`Client with ID ${id} not found`);
      }

      return client;
    } catch (error) {
      console.error(`❌ Error fetching client ${id}:`, error);
      throw error;
    }
  },

  async updateClient(
    clientId: string,
    data: {
      clientName?: string;
      instagramStartFollowers?: number;
      facebookStartFollowers?: number;
      relationshipStartedAt?: string | null;
      links?: Array<{ name: string; url: string }>;
      websiteUrl?: string | null;
      locale?: string | null;
      sector?: string | null;
      postsPerWeek?: number | null;
      latitude?: number | null;
      longitude?: number | null;
      location?: string | null;
      businessType?: string | null;
      serviceArea?: string | null;
      monthlyAdBudgetDefault?: number | null;
      autoBoostEnabled?: boolean;
      boostStrategy?: string | null;
      autoFetchIdeasEnabled?: boolean;
      isPortfolioVisible?: boolean;
      googleAdsCustomerId?: string | null;
      googleAdsClientPays?: boolean;
      googleAdsBilledFrom?: string | null;
      socialProfiles?: SocialProfiles;
    }
  ): Promise<Client> {
    try {
      const response = await apiClient.patch(`/client/${clientId}`, data);

      if (response.success) {
        return this._transformClient(response.client);
      }
      throw new Error(response.error || "Failed to update client");
    } catch (error: unknown) {
      console.error("Error updating client:", error);
      throw error;
    }
  },

  async archiveClient(clientId: string): Promise<Client> {
    try {
      const response = await apiClient.patch(`/client/${clientId}/status`, {
        status: "ARCHIVED",
      });

      if (response.success) {
        return this._transformClient(response.client);
      }
      throw new Error(response.error || "Failed to archive client");
    } catch (error: unknown) {
      console.error("Error archiving client:", error);
      throw error;
    }
  },

  async unarchiveClient(clientId: string): Promise<Client> {
    try {
      const response = await apiClient.patch(`/client/${clientId}/status`, {
        status: "ACTIVE",
      });

      if (response.success) {
        return this._transformClient(response.client);
      }
      throw new Error(response.error || "Failed to unarchive client");
    } catch (error: unknown) {
      console.error("Error unarchiving client:", error);
      throw error;
    }
  },

  async updateClientStatus(
    clientId: string,
    status: "ACTIVE" | "PROPOSAL" | "ARCHIVED",
  ): Promise<Client> {
    try {
      const response = await apiClient.patch(`/client/${clientId}/status`, {
        status,
      });

      if (response.success) {
        return this._transformClient(response.client);
      }
      throw new Error(response.error || "Failed to update client status");
    } catch (error: unknown) {
      console.error("Error updating client status:", error);
      throw error;
    }
  },

  _transformClient(rawClient: RawClientData): Client {
    const extendedRaw = rawClient as RawClientData & {
      links?: Array<{ name: string; url: string }>;
    };

    return {
      id: rawClient.uuid,
      clientName: rawClient.clientName,
      notionUrl: rawClient.notionUrl || "",
      createdAt: rawClient.createdAt,
      lastUpdated: rawClient.lastUpdated,
      status: rawClient.status as Client["status"],
      slug: rawClient.slug,
      proposalToken: rawClient.proposalToken,
      stats: rawClient.stats,
      links: extendedRaw.links || [],
      websiteUrl: rawClient.websiteUrl,
      socialProfiles: rawClient.socialProfiles,
      instagramStartFollowers: rawClient.instagramStartFollowers,
      facebookStartFollowers: rawClient.facebookStartFollowers,
      tiktokStartFollowers: rawClient.tiktokStartFollowers,
      relationshipStartedAt: rawClient.relationshipStartedAt,
      locale: rawClient.locale,
      sector: rawClient.sector,
      postsPerWeek: rawClient.postsPerWeek,
      latitude: rawClient.latitude,
      longitude: rawClient.longitude,
      location: rawClient.location,
      businessType: rawClient.businessType,
      serviceArea: rawClient.serviceArea,
      monthlyAdBudgetDefault: rawClient.monthlyAdBudgetDefault,
      autoBoostEnabled: rawClient.autoBoostEnabled,
      boostStrategy: rawClient.boostStrategy,
      autoFetchIdeasEnabled: rawClient.autoFetchIdeasEnabled,
      isPortfolioVisible: rawClient.isPortfolioVisible,
      googleAdsCustomerId: rawClient.googleAdsCustomerId,
      googleAdsClientPays: rawClient.googleAdsClientPays,
      googleAdsBilledFrom: rawClient.googleAdsBilledFrom,
      accessRole: rawClient.accessRole,
    };
  },
};
